import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { CardPage } from "../components/CardPage";
import { navigate } from "../router";
import { t } from "../i18n";

/**
 * Where an accepted invitation leads: a share's to /s/{id}, a recording's
 * (a reviewer's) to /r/{id}, an app template's to /t/{id} (RFC-052), a
 * file's to /f/{id} (RFC-054).
 */
export function inviteTarget(r: { share?: string; recording?: string; group?: string; template?: string; file?: string }): string {
  if (r.file) return `/f/${encodeURIComponent(r.file)}`;
  if (r.template) return `/t/${encodeURIComponent(r.template)}`;
  if (r.recording) return `/r/${encodeURIComponent(r.recording)}`;
  if (r.group) return `/g/${encodeURIComponent(r.group)}`;
  return `/s/${encodeURIComponent(r.share ?? "")}`;
}

/**
 * /i/{token}: accept the invitation (the person is signed in by now) and go to what it is for — at once,
 * unless it is to an agent share that keeps what its callers ask, which is said first and accepted only
 * once the person agrees (byj-1wv.119).
 */
export function Invite({ token }: { token: string }) {
  // why it failed, and whether it was the invitation itself, which the card's own words already say:
  // expired or used up (the relay's 410), or no invitation at that link, revoked or mistyped (404);
  // removed from the share (403) is said, as nothing else says it
  const [error, setError] = useState<{ text: string; gone: boolean } | null>(null);
  const [asking, setAsking] = useState(false);
  const started = useRef(false);

  const accept = () =>
    api
      .acceptInvite(token)
      .then((r) => navigate(inviteTarget(r), true))
      .catch((err: unknown) =>
        setError({ text: err instanceof Error ? err.message : String(err), gone: err instanceof ApiError && (err.status === 410 || err.status === 404) }),
      );

  useEffect(() => {
    // An invite may be single-use: never accept it twice (StrictMode runs effects twice).
    if (started.current) return;
    started.current = true;
    // reading it spends nothing; one that cannot be read is accepted, which says why not
    api
      .inviteInfo(token)
      .then((i) => (i.keeps_questions ? setAsking(true) : accept()))
      .catch(() => accept());
  }, [token]);

  if (asking) {
    return (
      <CardPage title={t("shares:inviteAgentTitle")} lead={t("shares:inviteKeepsQuestions")} back={{ to: "/shares", label: t("shares:inviteNotNow") }} testid="invite">
        <div>
          <button
            className="primary"
            data-testid="invite-agree"
            onClick={() => {
              setAsking(false);
              accept();
            }}
          >
            {t("shares:inviteAgree")}
          </button>
        </div>
      </CardPage>
    );
  }

  // an invitation to a share, a group, a recording, a template or a file: named as one, not a share's
  // alone; refused, said so in its title, why under it, and a way back (byj-1wv.51.7): the relay's
  // own words only for a failure the card's do not already say
  return error ? (
    <CardPage title={t("shares:inviteFailedTitle")} back={{ to: "/shares", label: t("common:backToShares") }} testid="invite">
      <p className="error flush">{t("shares:inviteFailed")}</p>
      {!error.gone && <p className="muted small flush">{error.text}</p>}
    </CardPage>
  ) : (
    <CardPage title={t("shares:inviteTitle")} lead={t("shares:inviteAccepting")} testid="invite" />
  );
}
